import { useRef } from "react";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { BRAND } from "@/shared/constants.js";
import { fmtDate, toMdY } from "@/shared/utils/dateHelpers.js";

// A MM/DD/YYYY text field with a calendar affordance on the right. Manual
// entry works exactly like the plain TextInput. The native date picker opens
// when the calendar area is tapped/clicked.
//
// Mobile-safe approach: rather than call showPicker() on a hidden input
// (unsupported on iOS Safari and flaky on Android), a REAL, transparent
// <input type="date"> is overlaid on top of the calendar-icon zone. Tapping
// the icon is therefore tapping the date input itself, which reliably opens
// the OS-native picker on every platform. The calendar glyph is drawn behind
// the transparent overlay. On desktop we also call showPicker() on click as a
// nicety, but it is not required for the picker to work.
export function DateInput({ value, onChange, placeholder = "MM/DD/YYYY", onKeyDown, onBlur, min, max }) {
  const dateRef = useRef(null);

  // Current MM/DD/YYYY → ISO (yyyy-mm-dd) so the picker opens on the entered
  // date; empty until a full 8-digit date is present.
  const iso = (() => {
    const m = fmtDate(value);
    if (!m) return "";
    const [mm, dd, yyyy] = m.split("/");
    return `${yyyy}-${mm}-${dd}`;
  })();

  const ICON_ZONE = 40; // px width of the tappable calendar area on the right

  return (
    <div style={{ position: "relative" }}>
      <TextInput
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode="numeric"
        autoComplete="off"
        style={{ paddingRight: ICON_ZONE + 6 }}
      />

      {/* Calendar glyph — purely visual, sits under the transparent date
          overlay so the icon appears inside the field. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", top: "50%", right: 11, transform: "translateY(-50%)",
          display: "inline-flex", pointerEvents: "none",
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </span>

      {/* Real native date input, transparent, overlaying ONLY the icon zone on
          the right. Tapping/clicking it opens the OS-native picker directly
          (works on iOS/Android/desktop). Selecting a date writes it back as
          MM/DD/YYYY. The rest of the field remains the text input for typing. */}
      <input
        ref={dateRef}
        type="date"
        value={iso}
        min={min}
        max={max}
        aria-label="Open date picker"
        onChange={(e) => onChange(toMdY(e.target.value))}
        onClick={() => { const el = dateRef.current; if (el && typeof el.showPicker === "function") { try { el.showPicker(); } catch { /* opened via native tap */ } } }}
        style={{
          position: "absolute", top: 0, right: 0, height: "100%", width: ICON_ZONE,
          margin: 0, padding: 0, border: 0, background: "transparent",
          color: "transparent", opacity: 0, cursor: "pointer",
          // hide the browser's own indicator; the glyph behind supplies the icon
          WebkitAppearance: "none", appearance: "none",
        }}
      />
    </div>
  );
}
